/**
 * Where to go back to after signing in: the page the visitor first asked
 * for (ProtectedRoute puts it in the login route's `state.from`) — but only
 * a same-origin relative path. One leading "/", never "//" or a backslash
 * (browsers read "/\evil.com" as "//evil.com"), no scheme, no control
 * characters, never an auth page. Anything else → null (use the default).
 */

const AUTH_PAGES = ['/login', '/register', '/forgot-password', '/reset-password', '/verify-email'];
const MAX_LENGTH = 2048;

/** A string path, or a router location { pathname, search, hash }. */
function toPath(value: unknown): string | null {
  if (typeof value === 'string') return value;
  if (!value || typeof value !== 'object') return null;
  const { pathname, search, hash } = value as Record<string, unknown>;
  if (typeof pathname !== 'string') return null;
  const extra = [search, hash].filter((part): part is string => typeof part === 'string');
  return pathname + extra.join('');
}

/** Resolved against any base, a same-origin path keeps that base's origin. */
const BASE = 'https://return-path.invalid';

function staysOnThisOrigin(path: string): boolean {
  try {
    return new URL(path, BASE).origin === BASE;
  } catch {
    return false;
  }
}

/** Same-origin relative: "/x…", not "//…", no backslash or control character. */
export function isSafeRelativePath(path: string): boolean {
  if (path.length > MAX_LENGTH || !path.startsWith('/') || path.startsWith('//')) return false;
  // eslint-disable-next-line no-control-regex -- rejecting control characters is the point
  if (/[\\\u0000-\u001f\u007f]/.test(path)) return false;
  return staysOnThisOrigin(path);
}

/** "/login", "/login?x", "/login#y" or "/login/…" — never back to a sign-in page. */
const isAuthPage = (path: string) =>
  AUTH_PAGES.some((page) => path.startsWith(page) && (path.length === page.length || '?#/'.includes(path[page.length])));

/** The safe path to return to, or null for the default landing page. */
export function safeReturnPath(from: unknown): string | null {
  const path = toPath(from);
  if (!path || !isSafeRelativePath(path) || isAuthPage(path)) return null;
  return path;
}

/** `state.from` of the login route, when there is one. */
export function returnPathFromState(state: unknown): string | null {
  if (!state || typeof state !== 'object' || !('from' in state)) return null;
  return safeReturnPath((state as { from: unknown }).from);
}
